/**
 * Strategic Behavioral Forecasting
 * ---------------------------------------------------------------
 * Read-only intelligence surface inside Admin → Intelligence.
 * Shows the most recent strategy cycle: dominant platform behavior,
 * emerging opportunity windows, saturation risk, fading archetypes,
 * escalation opportunities, and the active tactical directives.
 */
import { useCallback, useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2, Compass, AlertTriangle, ArrowDownRight, ArrowUpRight, Target } from "lucide-react";
import { fetchLatestStrategy, STRATEGY_ACCEPTED_EVENT } from "@/lib/strategyOrchestrator";

const titleCase = (s: string | null | undefined) =>
  (s ?? "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) || "—";

const StrategicBehavioralForecasting = () => {
  const [cycle, setCycle] = useState<any | null>(null);
  const [actions, setActions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await fetchLatestStrategy();
    setCycle(res?.cycle ?? null);
    setActions(res?.actions ?? []);
    setLoading(false);
  }, []);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    const h = () => void load();
    window.addEventListener(STRATEGY_ACCEPTED_EVENT, h);
    return () => window.removeEventListener(STRATEGY_ACCEPTED_EVENT, h);
  }, [load]);

  const fading = actions.filter((a) => a.action_type === "decrease" || a.action_type === "suppress");
  const rising = actions.filter((a) => a.action_type === "increase" || a.action_type === "favor");

  return (
    <Card className="bg-charcoal-medium/60 border-glass-border backdrop-blur-sm">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Compass className="w-4 h-4 text-cyan animate-pulse" />
          Strategic Behavioral Forecasting
          <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono ml-1">
            Autonomous generation strategy · current opportunity zone
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-cyan" /></div>
        ) : !cycle ? (
          <div className="text-[11px] font-mono text-muted-foreground text-center py-6">
            No strategy cycle yet · run the orchestrator in Admin → Viral to generate one
          </div>
        ) : (
          <div className="space-y-4">
            {/* Dominant zone */}
            <div className="rounded-lg border border-cyan/20 bg-charcoal-deep/60 p-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="text-[10px] uppercase tracking-[0.2em] text-cyan/80 font-mono">
                  Cycle #{cycle.cycle_number} · {cycle.status}
                </div>
                <Badge variant="outline" className="border-cyan/40 text-cyan font-mono text-[10px]">
                  Confidence {Math.round(cycle.confidence)}%
                </Badge>
              </div>
              <div className="text-sm text-foreground mt-2 italic">{cycle.strategic_priority}</div>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-4">
                {[
                  { label: "Platform", val: cycle.dominant_platform },
                  { label: "Archetype", val: cycle.dominant_archetype },
                  { label: "Format", val: cycle.dominant_format },
                  { label: "Escalation", val: cycle.dominant_escalation },
                  { label: "Pacing", val: cycle.dominant_pacing },
                ].map((m) => (
                  <div key={m.label}>
                    <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-mono">{m.label}</div>
                    <div className="text-xs font-mono text-foreground mt-0.5">{titleCase(m.val)}</div>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-3 mt-4 pt-3 border-t border-glass-border">
                <div>
                  <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-mono">Projected Virality</div>
                  <div className="text-xl font-mono text-cyan mt-0.5">{Math.round(cycle.projected_virality)}</div>
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-mono">Projected Replay</div>
                  <div className="text-xl font-mono text-amber-300 mt-0.5">{Math.round(cycle.projected_replay_rate)}%</div>
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-mono">Saturation Risk</div>
                  <div className={`text-xl font-mono mt-0.5 ${cycle.saturation_risk > 60 ? "text-red-300" : cycle.saturation_risk > 35 ? "text-amber-300" : "text-emerald-300"}`}>
                    {Math.round(cycle.saturation_risk)}%
                  </div>
                </div>
              </div>
            </div>

            {/* Rising / Fading */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="rounded-lg border border-glass-border bg-charcoal-deep/40 p-3">
                <div className="text-[10px] uppercase tracking-wider text-emerald-300 font-mono mb-2 flex items-center gap-1">
                  <ArrowUpRight className="w-3 h-3" />Emerging Opportunity Windows
                </div>
                {rising.length === 0 ? (
                  <div className="text-[11px] font-mono text-muted-foreground">No rising signals.</div>
                ) : (
                  <div className="space-y-1.5">
                    {rising.map((a) => (
                      <div key={a.id} className="flex items-center justify-between text-[11px] font-mono">
                        <span className="text-foreground truncate max-w-[60%]">
                          {titleCase(a.target)}
                          <span className="text-[10px] text-muted-foreground ml-1">· {a.dimension}</span>
                        </span>
                        <span className="text-emerald-300">+{Math.round(a.adjustment * 100)}% · {Math.round(a.confidence)}%</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="rounded-lg border border-glass-border bg-charcoal-deep/40 p-3">
                <div className="text-[10px] uppercase tracking-wider text-amber-300 font-mono mb-2 flex items-center gap-1">
                  <ArrowDownRight className="w-3 h-3" />Fading Archetypes · Saturation Guards
                </div>
                {fading.length === 0 ? (
                  <div className="text-[11px] font-mono text-muted-foreground">Nothing fading yet.</div>
                ) : (
                  <div className="space-y-1.5">
                    {fading.map((a) => (
                      <div key={a.id} className="flex items-center justify-between text-[11px] font-mono">
                        <span className="text-foreground truncate max-w-[60%]">
                          {titleCase(a.target)}
                          <span className="text-[10px] text-muted-foreground ml-1">· {a.dimension}</span>
                        </span>
                        <span className="text-amber-300">{Math.round(a.adjustment * 100)}% · {Math.round(a.confidence)}%</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Tactical directives */}
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground/80 font-mono mb-2 flex items-center gap-1">
                <Target className="w-3 h-3 text-cyan" />Tactical Generation Directives
              </div>
              <ScrollArea className="max-h-[240px]">
                <div className="divide-y divide-glass-border">
                  {actions.length === 0 ? (
                    <div className="text-[11px] font-mono text-muted-foreground py-3 text-center">No directives.</div>
                  ) : actions.map((a) => (
                    <div key={a.id} className="py-2 flex items-start gap-3">
                      <Badge variant="outline" className="text-[9px] uppercase font-mono border-cyan/30 text-cyan/80 shrink-0">
                        {a.action_type}
                      </Badge>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-foreground">
                          {titleCase(a.target)}
                          <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-mono ml-2">{a.dimension}</span>
                        </div>
                        <div className="text-[10px] text-muted-foreground italic mt-0.5">{a.reasoning}</div>
                      </div>
                      <div className="text-[10px] font-mono text-cyan shrink-0">{Math.round(a.confidence)}%</div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>

            {cycle.saturation_risk > 60 && (
              <div className="flex items-center gap-2 p-3 border border-amber-400/30 bg-amber-400/5 rounded-lg">
                <AlertTriangle className="w-4 h-4 text-amber-300" />
                <div className="text-[11px] font-mono text-amber-200">
                  Saturation alert · platform may begin de-prioritizing this pattern. Diversify next cycle.
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default StrategicBehavioralForecasting;
